/**
 * tomtomTrafficTiles — utilidad para consumir los **Vector Flow Tiles**
 * de TomTom (PBF) y devolverlos en coordenadas WGS84 listos para pintar
 * como `Polyline3DInteractiveElement` en la vista realista Google 3D.
 *
 * Endpoint: `https://api.tomtom.com/traffic/map/4/tile/flow/relative/{z}/{x}/{y}.pbf?key=…`
 *
 * Cada tile contiene una capa "Traffic flow" con `LineString` por segmento
 * de calle. Atributos relevantes por feature:
 *  - `traffic_level` (number 0..1): ratio currentSpeed/freeFlowSpeed.
 *  - `road_closure` (0|1): tramo cerrado.
 *  - `road_type` (string).
 *
 * Web Mercator estándar para tile <-> WGS84. Coords internas del tile en
 * el rango 0..extent (en estas tiles `extent=4096`, lo expone la lib).
 */
import { VectorTile } from '@mapbox/vector-tile';
import { PbfReader } from 'pbf';

export interface BBox {
  west: number;
  south: number;
  east: number;
  north: number;
}

export interface TrafficSegment {
  coordinates: Array<{ lat: number; lng: number }>;
  /** 0..1 — ratio currentSpeed/freeFlowSpeed según TomTom. */
  trafficLevel: number;
  roadClosure: boolean;
  roadType?: string;
}

interface TileXY {
  x: number;
  y: number;
}

/* ──────────────── Web Mercator helpers ──────────────── */

function lon2tile(lon: number, zoom: number): number {
  return Math.floor(((lon + 180) / 360) * Math.pow(2, zoom));
}

function lat2tile(lat: number, zoom: number): number {
  const rad = (lat * Math.PI) / 180;
  return Math.floor(
    ((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) *
      Math.pow(2, zoom),
  );
}

function tile2lon(x: number, zoom: number): number {
  return (x / Math.pow(2, zoom)) * 360 - 180;
}

function tile2lat(y: number, zoom: number): number {
  const n = Math.PI - (2 * Math.PI * y) / Math.pow(2, zoom);
  return (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
}

/** Devuelve los (x,y) que cubren `bbox` a `zoom`. */
export function tilesForBbox(bbox: BBox, zoom: number): TileXY[] {
  const xMin = lon2tile(bbox.west, zoom);
  const xMax = lon2tile(bbox.east, zoom);
  // OJO: lat2tile invierte el eje (lat mayor → y menor).
  const yMin = lat2tile(bbox.north, zoom);
  const yMax = lat2tile(bbox.south, zoom);
  const tiles: TileXY[] = [];
  for (let x = xMin; x <= xMax; x++) {
    for (let y = yMin; y <= yMax; y++) {
      tiles.push({ x, y });
    }
  }
  return tiles;
}

/* ──────────────── Fetch + parse ──────────────── */

/** Convierte coords tile-locales (0..extent) a WGS84 lat/lng. */
function tileLocalToLatLng(
  px: number,
  py: number,
  tx: number,
  ty: number,
  zoom: number,
  extent: number,
): { lat: number; lng: number } {
  const lng = tile2lon(tx + px / extent, zoom);
  const lat = tile2lat(ty + py / extent, zoom);
  return { lat, lng };
}

/**
 * Descarga + parsea un tile y devuelve los segmentos en lat/lng.
 * Lanza si HTTP no es 2xx (429 rate-limit, 5xx, etc.) — el caller decide
 * cómo responder al fallo.
 */
export async function fetchTrafficTile(
  z: number,
  x: number,
  y: number,
  apiKey: string,
): Promise<TrafficSegment[]> {
  const url = `https://api.tomtom.com/traffic/map/4/tile/flow/relative/${z}/${x}/${y}.pbf?key=${apiKey}`;
  const res = await fetch(url, { cache: 'no-cache' });
  if (!res.ok) {
    throw new Error(`TomTom traffic tile ${z}/${x}/${y} → HTTP ${res.status}`);
  }
  const buf = await res.arrayBuffer();
  const tile = new VectorTile(new PbfReader(new Uint8Array(buf)));
  // La capa puede llamarse "Traffic flow" o "Traffic_flow" según versión —
  // tomamos la primera (o única) capa de la tile.
  const layerName = Object.keys(tile.layers)[0];
  if (!layerName) return [];
  const layer = tile.layers[layerName];
  const extent = layer.extent ?? 4096;
  const out: TrafficSegment[] = [];
  for (let i = 0; i < layer.length; i++) {
    const feature = layer.feature(i);
    const geom = feature.loadGeometry(); // Array<Array<{x,y}>>
    const props = feature.properties as Record<string, unknown>;
    const trafficLevel = typeof props.traffic_level === 'number' ? props.traffic_level : 1;
    const roadClosure = props.road_closure === 1 || props.road_closure === true;
    const roadType = typeof props.road_type === 'string' ? props.road_type : undefined;
    for (const ring of geom) {
      if (ring.length < 2) continue;
      const coordinates = ring.map(p => tileLocalToLatLng(p.x, p.y, x, y, z, extent));
      out.push({ coordinates, trafficLevel, roadClosure, roadType });
    }
  }
  return out;
}
